import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import FloatingBubbles from '../components/FloatingBubbles';
import axios from 'axios';

const TYPES = [
  { value: 'idee', label: '💡 Idée', color: '#276749', bg: '#F0FFF4', border: '#68D391' },
  { value: 'bug', label: '🐛 Bug', color: '#9B2C2C', bg: '#FFF5F5', border: '#FC8181' },
  { value: 'amelioration', label: '⚡ Amélioration', color: '#2B6CB0', bg: '#EBF4FF', border: '#4299E1' },
];

const STATUSES = {
  en_attente: { label: 'En attente', color: '#C05621', bg: '#FFFAF0', border: '#F6AD55' },
  en_cours: { label: '🔄 En cours', color: '#2B6CB0', bg: '#EBF4FF', border: '#4299E1' },
  resolu: { label: '✓ Résolu', color: '#276749', bg: '#F0FFF4', border: '#68D391' },
  refuse: { label: 'Refusé', color: '#718096', bg: '#F7F9FC', border: '#CBD5E0' },
};

export default function Suggestions() {
  const { user, token, API } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', suggestion_type: 'idee' });
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const fetchSuggestions = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/suggestions`, { headers });
      setSuggestions(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [API, token]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/suggestions`, form, { headers });
      setForm({ title: '', description: '', suggestion_type: 'idee' });
      setShowForm(false);
      fetchSuggestions();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (id) => {
    if (!token) return;
    try {
      const res = await axios.post(`${API}/suggestions/${id}/vote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSuggestions(prev => prev.map(s => s.id === id ? { ...s, voted_by_me: res.data.voted, votes: res.data.voted ? s.votes + 1 : s.votes - 1 } : s));
    } catch (err) { console.error(err); }
  };

  const filtered = (filterType === 'all' ? suggestions : suggestions.filter(s => s.suggestion_type === filterType)).sort((a, b) => b.votes - a.votes);

  return (
    <>
      <FloatingBubbles />
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>

        {/* Hero */}
        <div style={{ background: 'rgba(235,244,255,0.85)', backdropFilter: 'blur(4px)', padding: '48px 20px 32px', textAlign: 'center', borderBottom: '1px solid rgba(190,227,248,0.5)' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>💡</div>
          <h1 style={{ fontSize: '32px', fontWeight: 800, color: 'var(--dark)', letterSpacing: '-1px', marginBottom: '10px' }}>Suggestions</h1>
          <p style={{ fontSize: '15px', color: 'var(--text)', maxWidth: '480px', margin: '0 auto 24px', lineHeight: 1.7 }}>
            Une idée pour améliorer Agora ? Un bug à signaler ? Partage-le ici.
          </p>
          <button onClick={() => setShowForm(!showForm)} style={{ background: showForm ? 'var(--border)' : 'var(--blue)', color: showForm ? 'var(--text)' : '#fff', border: 'none', padding: '12px 28px', borderRadius: '24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: showForm ? 'none' : '0 4px 16px rgba(43,108,176,0.3)', transition: 'all .2s' }}>
            {showForm ? '✕ Annuler' : '+ Nouvelle suggestion'}
          </button>
        </div>

        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 20px 48px' }}>

          {/* Form */}
          {showForm && (
            <div style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderRadius: '16px', padding: '24px', border: '1px solid var(--blue-border)', boxShadow: 'var(--shadow)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dark)', marginBottom: '16px' }}>Nouvelle suggestion</h3>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => setForm({ ...form, suggestion_type: t.value })} style={{ background: form.suggestion_type === t.value ? t.bg : 'transparent', color: form.suggestion_type === t.value ? t.color : 'var(--muted)', border: `1.5px solid ${form.suggestion_type === t.value ? t.border : 'var(--border)'}`, padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s' }}>
                      {t.label}
                    </button>
                  ))}
                </div>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titre de la suggestion..." required
                  style={{ padding: '11px 16px', border: '1.5px solid var(--blue-border)', borderRadius: '10px', fontSize: '14px', outline: 'none', fontWeight: 600 }}
                  onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                />
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Décris ta suggestion en détail..." required rows={4}
                  style={{ padding: '11px 16px', border: '1.5px solid var(--blue-border)', borderRadius: '10px', fontSize: '14px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                  onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                />
                {!user && <p style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>Tu peux soumettre sans compte, mais tu ne pourras pas voter.</p>}
                <button type="submit" disabled={submitting || !form.title.trim() || !form.description.trim()} style={{ background: 'var(--blue)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(43,108,176,0.25)', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Envoi...' : 'Envoyer →'}
                </button>
              </form>
            </div>
          )}

          {/* Filter by type */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button onClick={() => setFilterType('all')} style={{ background: filterType === 'all' ? 'var(--blue)' : 'transparent', color: filterType === 'all' ? '#fff' : 'var(--muted)', border: `1px solid ${filterType === 'all' ? 'var(--blue)' : 'var(--border)'}`, padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Tous ({suggestions.length})
            </button>
            {TYPES.map(t => (
              <button key={t.value} onClick={() => setFilterType(t.value)} style={{ background: filterType === t.value ? t.bg : 'transparent', color: filterType === t.value ? t.color : 'var(--muted)', border: `1px solid ${filterType === t.value ? t.border : 'var(--border)'}`, padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                {t.label} ({suggestions.filter(s => s.suggestion_type === t.value).length})
              </button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--muted)' }}>Chargement...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--muted)' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
              <p>Aucune suggestion pour l'instant. Sois le premier !</p>
            </div>
          ) : filtered.map(s => {
            const t = TYPES.find(t => t.value === s.suggestion_type) || TYPES[0];
            const st = STATUSES[s.status] || STATUSES.en_attente;
            return (
              <div key={s.id} style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderRadius: '16px', padding: '20px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', marginBottom: '12px', transition: 'border-color .2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue-border)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  {/* Vote */}
                  <button onClick={() => handleVote(s.id)} disabled={!token} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', background: s.voted_by_me ? 'var(--blue-bg)' : 'var(--bg)', border: `1.5px solid ${s.voted_by_me ? 'var(--blue)' : 'var(--border)'}`, borderRadius: '10px', padding: '8px 10px', cursor: token ? 'pointer' : 'default', transition: 'all .2s', minWidth: '48px', flexShrink: 0 }}>
                    <span style={{ fontSize: '14px', color: s.voted_by_me ? 'var(--blue)' : 'var(--muted)' }}>{s.voted_by_me ? '▲' : '△'}</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: s.voted_by_me ? 'var(--blue)' : 'var(--dark)' }}>{s.votes}</span>
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <span style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}`, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>{t.label}</span>
                      <span style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>{st.label}</span>
                    </div>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--dark)', marginBottom: '6px', lineHeight: 1.4 }}>{s.title}</h3>
                    <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: 1.7, margin: '0 0 8px' }}>{s.description}</p>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      par <span style={{ fontWeight: 600, color: 'var(--blue)' }}>@{s.author}</span> · {new Date(s.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
